/**
 * AnalysisCard — json-render web wrapper
 *
 * Web implementation for the json-render catalog (web-only).
 * Visual spec: Sally (Discourse #3543/4) — Section 1
 *
 * Upgraded to glassmorphism container with variant-based styling:
 * - backdrop-blur glassmorphism backgrounds
 * - standard / elevated / premium variants
 * - Premium variant has gold border + subtle glow
 *
 * Task: [SDUI-M.P3.3]
 * Agent: Diego <diego@liftping.net>
 */
"use client";

import React from "react";
import { cn } from "../../lib/utils";

type AnalysisCardVariant = "standard" | "elevated" | "premium";
type AnalysisTier = "free" | "drama_plus" | "dramax";

export interface AnalysisCardComponentProps {
  title: string;
  subtitle?: string;
  variant: AnalysisCardVariant;
  tierHint?: AnalysisTier;
  children?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Variant style map                                                  */
/* ------------------------------------------------------------------ */

const variantClasses: Record<AnalysisCardVariant, string> = {
  standard:
    "backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-6",
  elevated:
    "backdrop-blur-md bg-white/[0.07] border border-white/15 rounded-2xl p-6",
  premium: [
    "backdrop-blur-md bg-white/[0.07] rounded-2xl p-6",
    "border border-[#C8A882]/50",
    "shadow-[0_0_20px_rgba(200,168,130,0.08)]",
  ].join(" "),
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function AnalysisCard({
  title,
  subtitle,
  variant,
  children,
}: AnalysisCardComponentProps) {
  return (
    <div className={cn(variantClasses[variant], "flex flex-col")}>
      {(title || subtitle) && (
        <div className="flex flex-col gap-1 mb-4">
          {title && (
            <h3 className="text-xl font-semibold leading-none tracking-tight text-[#E5E7EB]">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm text-[#9CA3AF]">{subtitle}</p>
          )}
        </div>
      )}
      {children && <div className="space-y-4">{children}</div>}
    </div>
  );
}
